/**
 * MHR Document Types
 *
 * Types for medical history documents, upload handling, and cost estimates.
 */

export type ExtractionStatus =
  | 'pending'
  | 'extracting'
  | 'extracted'
  | 'ocr_pending'
  | 'ocr_in_progress'
  | 'vision_verification'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export type DocumentCategory =
  | 'medical_records'
  | 'lab_results'
  | 'radiology'
  | 'pathology'
  | 'operative_notes'
  | 'discharge_summary'
  | 'progress_notes'
  | 'medication_list'
  | 'referral'
  | 'consent_form'
  | 'insurance'
  | 'source_document'
  | 'screening_form'
  | 'other';

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  medical_records: 'Medical Records',
  lab_results: 'Lab Results',
  radiology: 'Radiology',
  pathology: 'Pathology',
  operative_notes: 'Operative Notes',
  discharge_summary: 'Discharge Summary',
  progress_notes: 'Progress Notes',
  medication_list: 'Medication List',
  referral: 'Referral',
  consent_form: 'Consent Form',
  insurance: 'Insurance',
  source_document: 'Source Document',
  screening_form: 'Screening Form',
  other: 'Other',
};

export interface MHRDocument {
  id: string;
  patient_id: string;
  site_id: string;
  filename: string;
  file_size: number;
  page_count: number;
  extraction_status: ExtractionStatus;
  extraction_error?: string;
  uploaded_at: string;
  uploaded_by?: string;
  // Document metadata
  document_type?: string;
  provider_name?: string;
  // Categorization
  document_category?: DocumentCategory;
  document_date?: string;
  date_range_start?: string;
  date_range_end?: string;
  // Source metadata
  facility_name?: string;
  description?: string;
  tags?: string[];
  // Duplicate detection
  content_hash?: string;
  is_duplicate?: boolean;
  duplicate_of_id?: string;
  // AI-extracted metadata
  ai_document_category?: string;
  ai_document_date?: string;
  ai_facility_name?: string;
  ai_description?: string;
  // Processing costs
  ocr_cost?: number;
  vision_cost?: number;
  extraction_quality_score?: number;
  ocr_completed_at?: string;
  vision_completed_at?: string;
  // Search results (populated by search endpoint)
  matching_pages?: number[];
  match_count?: number;
}

export interface CostEstimate {
  id: string;
  extraction_cost: number;
  ocr_cost: number;
  vision_verification_cost: number;
  analysis_cost: number;
  total_estimated_cost: number;
  page_count: number;
  criteria_count: number;
  estimated_duration_minutes: number;
  approval_required: boolean;
  breakdown: Record<string, number>;
}

export interface UploadFile {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  document?: MHRDocument;
}

export interface MHRPatient {
  id: string;
  site_id: string;
  internal_id: string;
  mrn?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  dob?: string;
  created_at: string;
  created_by: string;
  last_activity_at?: string;
}

export interface DocumentProcessingStatus {
  id: string;
  status: MHRDocument['extraction_status'];
  page_count: number;
  pages_processed: number;
  error?: string;
  estimated_completion?: string;
}
